import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/admin/AdminUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const ROLE_LABELS = { admin: "Administrateur", direction: "Direction", marketing: "Marketing", commercial: "Commercial", editor: "Éditeur", support: "Support" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => toast.error("Accès réservé aux administrateurs"));
  useEffect(() => { load(); api.get("/admin/roles").then(({ data }) => setRoles(data)).catch(() => {}); }, []);

  const save = async () => {
    if (form.id) {
      const body = { name: form.name, role: form.role, active: form.active };
      if (form.password) body.password = form.password;
      try { await api.put(`/admin/users/${form.id}`, body); toast.success("Utilisateur modifié"); setOpen(false); load(); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      return;
    }
    if (!form.name || !form.email || !form.password) { toast.error("Tous les champs sont requis"); return; }
    try { await api.post("/admin/users", form); toast.success("Utilisateur créé"); setOpen(false); setForm({ name: "", email: "", password: "", role: "editor" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Utilisateurs & Rôles" subtitle="Gérez les accès de votre équipe."
        action={<button onClick={() => { setForm({ name: "", email: "", password: "", role: "editor" }); setOpen(true); }} data-testid="user-add-button" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold"><Plus className="w-4 h-4" /> Ajouter</button>} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {(roles.length ? roles : Object.entries(ROLE_LABELS).map(([key, label]) => ({ key, label }))).map((r) => (
          <Card key={r.key} className="p-4 flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#4CB944]" /><div><div className="text-sm font-medium text-white">{r.label}</div><div className="text-xs text-slate-500 capitalize">{r.key}</div></div></Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        {users.length === 0 ? <EmptyState text="Aucun utilisateur." /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="users-table">
            <thead><tr className="border-b border-[#1E293B] text-left text-slate-400"><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>{users.map((u) => (
              <tr key={u.id} className="border-b border-[#1E293B]/60">
                <td className="px-4 py-3 text-white">{u.name}</td>
                <td className="px-4 py-3 text-slate-300">{u.email}</td>
                <td className="px-4 py-3"><span className="px-2.5 py-1 rounded-full text-xs bg-[#3B82F6]/15 text-[#3B82F6]">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td className="px-4 py-3"><span className={`text-xs ${u.active === false ? "text-rose-400" : "text-emerald-400"}`}>{u.active === false ? "Désactivé" : "Actif"}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => { setForm({ id: u.id, name: u.name, email: u.email, password: "", role: u.role, active: u.active !== false }); setOpen(true); }} data-testid={`user-edit-${u.id}`} className="p-1.5 rounded-lg text-slate-500 hover:text-white"><Pencil className="w-4 h-4" /></button>
                  {u.role !== "admin" && <button onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="dark bg-[#111827] border-[#1E293B]">
          <DialogHeader><DialogTitle className="text-white">{form.id ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm text-slate-300">Nom</label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" /></div>
            <div><label className="text-sm text-slate-300">Email</label><Input data-testid="user-email" type="email" disabled={!!form.id} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" /></div>
            <div><label className="text-sm text-slate-300">{form.id ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}</label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" /></div>
            <div><label className="text-sm text-slate-300">Rôle</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1.5 px-3 py-2 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-white text-sm">
                {Object.entries(ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {form.id && <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="user-active" /> Compte actif</label>}
          </div>
          <DialogFooter><button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl border border-[#1E293B] text-slate-300 text-sm">Annuler</button><button onClick={save} data-testid="user-save-button" className="px-5 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold">{form.id ? "Enregistrer" : "Créer"}</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
